import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Reveal } from "@/components/Reveal";
import { fmtDate } from "@/components/PostCard";
import api from "@/lib/api";

export default function Writing() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setPost(null);
    setNotFound(false);
    api
      .get(`/posts/${slug}`)
      .then((r) => setPost(r.data))
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <main className="wrap flex min-h-[70vh] flex-col justify-center py-32">
        <p className="overline-mono">404 — Unpublished</p>
        <h1 className="mb-8 font-serif text-3xl text-navy sm:text-5xl">This piece has moved or is unpublished.</h1>
        <div className="flex gap-4">
          <Link to="/research" className="btn-ghost">Research</Link>
          <Link to="/blog" className="btn-ghost">Essays</Link>
        </div>
      </main>
    );
  }

  if (!post) {
    return (
      <main className="wrap flex min-h-[70vh] items-center justify-center">
        <p className="text-[11px] font-semibold uppercase tracking-[0.25em] text-navy-mute">Fetching the manuscript…</p>
      </main>
    );
  }

  const blocks = (post.content || "").split("\n\n");

  return (
    <main className="pt-28 lg:pt-36">
      <article className="wrap max-w-3xl pb-24">
        <Reveal>
          <div className="flex flex-wrap items-center gap-4 text-[10px] font-semibold uppercase tracking-[0.2em]">
            <Link to={post.type === "paper" ? "/research" : "/blog"} className="text-blue transition-colors hover:text-blue-deep">
              {post.type === "paper" ? "Research & Papers" : "Blog & Articles"}
            </Link>
            <span className="text-navy-mute">/</span>
            <span className="text-navy">{post.type === "paper" ? "Research Paper" : "Essay"}</span>
            <span className="text-navy-mute">· {post.category}</span>
          </div>
          <h1 className="mt-6 font-serif text-4xl leading-[1.1] tracking-tight text-navy sm:text-5xl">
            {post.title}
          </h1>
          <p className="mt-6 border-b border-line pb-6 text-[10px] font-semibold uppercase tracking-[0.18em] text-navy-mute">
            {fmtDate(post.date)} · {post.read_time} min read · All Elite Research
          </p>
        </Reveal>

        {post.cover_url && (
          <Reveal delay={0.05}>
            <div className="mt-10 overflow-hidden border border-line">
              <img src={post.cover_url} alt="" className="aspect-[16/9] w-full object-cover" />
            </div>
          </Reveal>
        )}

        {post.excerpt && (
          <Reveal delay={0.05}>
            <div className={`mt-10 p-6 sm:p-8 ${post.type === "paper" ? "border-l-2 border-blue bg-paper-band" : ""}`}>
              <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-blue">
                {post.type === "paper" ? "Abstract" : "In one line"}
              </p>
              <p className="mt-3 font-serif text-lg italic leading-relaxed text-navy sm:text-xl">{post.excerpt}</p>
            </div>
          </Reveal>
        )}

        <div className="mt-12">
          {blocks.map((b, i) => {
            if (b.startsWith("## ")) {
              return (
                <h2 key={i} className="mb-5 mt-12 font-serif text-2xl text-navy sm:text-3xl">
                  {b.slice(3)}
                </h2>
              );
            }
            if (b.startsWith("> ")) {
              return (
                <blockquote key={i} className="my-10 border-l-2 border-blue pl-6 font-serif text-xl italic leading-relaxed text-navy sm:text-2xl">
                  {b.slice(2)}
                </blockquote>
              );
            }
            return (
              <p key={i} className="mb-6 leading-[1.85] text-navy-soft sm:text-lg">
                {b}
              </p>
            );
          })}
        </div>

        {post.tags?.length > 0 && (
          <div className="mt-14 flex flex-wrap gap-2 border-t border-line pt-8">
            {post.tags.map((t) => (
              <span key={t} className="border border-line px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-navy-soft">
                {t}
              </span>
            ))}
          </div>
        )}

        <p className="mt-16 text-center font-serif text-2xl text-blue">◆</p>
      </article>
    </main>
  );
}